import { CategorizationMap } from "./category";
import { Category } from "protos/robot_syncer/plant_profile";
import { CropThumbnailPlacement } from "portal/state/imageServiceApi";
import { DateTime } from "luxon";
import { i18n as I18n } from "i18next";
import { PredictionPoint } from "protos/veselka/prediction_point";
import { UnsavedPlantProfile } from "./plantProfile";

export const DEFAULT_AVERAGE_PPCM = 78.740_16; // 200px/in
export const DEFAULT_CHIP_UPLOAD_SIZE = 600;
export const MIN_THUMBNAIL_SIZE = 50;
export const MAX_THUMBNAIL_SIZE = 400;

export interface Dimensions {
  width: number;
  height: number;
}

/** Creates a convenience lookup for which images are categorized */
export const imagesToCategoryIds = (
  categories: Category[] | undefined
): CategorizationMap => {
  const categoryMap: CategorizationMap = {};
  if (!categories) {
    return categoryMap;
  }
  for (const { id: categoryId, chipIds } of categories) {
    for (const pointId of chipIds) {
      categoryMap[pointId] = categoryId;
    }
  }
  return categoryMap;
};

/**
 * Toggle a chip id on a category's chip list.
 * Removes if present; otherwise prepends when allowAdd is true.
 * Prepend (not append) so the change is easier to see on horizontal scroll.
 */
const toggleCategoryChipIds = (
  chipIds: readonly string[],
  pointId: string,
  allowAdd: boolean
): string[] => {
  const updatedChipIds = new Set(chipIds);
  const exists = updatedChipIds.has(pointId);
  if (exists) {
    updatedChipIds.delete(pointId);
  } else if (allowAdd) {
    return [pointId, ...updatedChipIds];
  }
  return [...updatedChipIds];
};

export const plantProfileImageToggle = (
  profile: UnsavedPlantProfile,
  activeCategoryId: string | undefined,
  pointId: string
): UnsavedPlantProfile => {
  if (!activeCategoryId || !pointId) {
    return profile;
  }

  return {
    ...profile,
    categories: profile.categories.map((category) => ({
      ...category,
      chipIds: toggleCategoryChipIds(
        category.chipIds,
        pointId,
        category.id === activeCategoryId
      ),
    })),
  };
};

export const categoryImageToggle = (
  category: Category,
  pointId: string
): Category => {
  if (!pointId) {
    return category;
  }
  return {
    ...category,
    chipIds: toggleCategoryChipIds(category.chipIds, pointId, true),
  };
};

const DEFAULT_PADDING_FACTOR = 0.1;
const RADIUS_CUTOFF_PX = 100;
const MINIMUM_DIM_PX = RADIUS_CUTOFF_PX * 2;

export const getThumbnailPaddingPx = (
  radius: number,
  radiusCutoff: number = RADIUS_CUTOFF_PX,
  minDimensionsPx: number = MINIMUM_DIM_PX,
  largeImagePaddingFactor: number = DEFAULT_PADDING_FACTOR
): number => {
  return radius < radiusCutoff
    ? Math.ceil(minDimensionsPx / 2 - radius)
    : Math.ceil(radius * largeImagePaddingFactor);
};

const derivePaddingFactor = (
  thumbnailWidth: number,
  paddingPx: number
): number => (thumbnailWidth ? paddingPx / (thumbnailWidth / 2) : 0);

/**
 * Given a prediction point, create a cropping that shows as much as the plant
 * as possible while also balancing image clarity
 */
export const getPlantAwareCropping = (
  {
    x: centerX,
    y: centerY,
    radius,
  }: Pick<PredictionPoint, "x" | "y" | "radius">,
  radiusCutoff: number = RADIUS_CUTOFF_PX,
  minDimensionsPx: number = MINIMUM_DIM_PX,
  largeImagePaddingFactor: number = DEFAULT_PADDING_FACTOR
): CropThumbnailPlacement & {
  paddingFactor: number;
} => {
  const paddingPx = getThumbnailPaddingPx(
    radius,
    radiusCutoff,
    minDimensionsPx,
    largeImagePaddingFactor
  );
  const imageCropDimensions = Math.ceil((radius + paddingPx) * 2);
  const paddingFactor = derivePaddingFactor(imageCropDimensions, paddingPx);
  const topLeftCoord =
    radius < radiusCutoff
      ? {
          x: Math.ceil(centerX - minDimensionsPx / 2),
          y: Math.ceil(centerY - minDimensionsPx / 2),
        }
      : {
          x: Math.ceil(centerX - (radius + paddingPx)),
          y: Math.ceil(centerY - (radius + paddingPx)),
        };
  return {
    ...topLeftCoord,
    width: imageCropDimensions,
    height: imageCropDimensions,
    paddingFactor,
  };
};

/**
 * Derive simple square cropping data based on center
 */
export const getCropping = (
  centerX: number,
  centerY: number,
  dimension = 800
): CropThumbnailPlacement => {
  const topLeftCoord = {
    x: Math.ceil(centerX - dimension / 2),
    y: Math.ceil(centerY - dimension / 2),
  };
  return {
    ...topLeftCoord,
    width: dimension,
    height: dimension,
  };
};

export const userFacingDate = (i18n: I18n, date: number): string =>
  DateTime.fromMillis(date).toLocaleString(
    {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    },
    { locale: i18n.language }
  );
